import { AgentQueues, Queue } from '@nice-ccf/common-sdk';
import { AgentSessionManager, ApiUriConstants, HttpClient, HttpPollingRequestInit,
  HttpUtilService, Logger, UrlUtilsService } from '@nice-ccf/core-sdk';
import { wrap } from 'comlink';
import { CXoneClient } from '../cxone-client';
import { AgentQueuesWorker } from '../worker/cxone-agent-queues-worker';

/**
 * Agent Queues Provider Class
 */
export class CXoneAgentQueuesProvider {
  private logger: Logger = new Logger('SDK', 'CXoneAgentQueuesProvider');
  private agentSession = AgentSessionManager.instance;
  private baseUri = '';
  private utilService: HttpUtilService = new HttpUtilService();
  private cxoneClient = <CXoneClient>{};
  private urlUtilService: UrlUtilsService = new UrlUtilsService();

  public pollingWorker: any;

  /**
   * Used to set the directory base instance to access the subject from the base class
   * @example -
   * ```
   * const agentQueuesProvider = new CXoneAgentQueuesProvider();
   * agentQueuesProvider.setACDSdkBaseInstance(this);
   * ```
   */
  public setACDSdkBaseInstance(cxoneClient: CXoneClient) {
    this.cxoneClient = cxoneClient;
  }

  /**
   * Used to initiate the polling for agent queue data
   * @example -
   * ```
   * const agentQueuesProvider = new CXoneAgentQueuesProvider();
   * this.agentQueuesProvider.agentQueuesPolling();
   * ```
   */
  public agentQueuesPolling(agentId: string) {
    if (this.pollingWorker) {
      this.logger.info(
        'agentQueuesPolling',
        'agentQueuesPolling is already started'
      );
      return;
    }
    this.logger.info(
      'agentQueuesPolling',
      'agentQueuesPolling in CXoneAgentQueuesProvider'
    );
    this.baseUri = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    const requestParams = {
      fields: '',
      updatedSince: new Date(0).toISOString(),
    };
    if (this.baseUri && authToken) {
      const { startAgentQueuesPolling } = this.initAgentQueuesWorker();
      const queueUri = ApiUriConstants.AGENT_QUEUE_URI.replace(
        '{agentId}',
        agentId
      );
      const url =
        this.baseUri +
        this.urlUtilService.appendQueryString(queueUri, requestParams);

      const reqInit: HttpPollingRequestInit = {
        headers: this.utilService.initPollingHeader(
          authToken,
          'application/json'
        ),
        method: 'GET',
      };
      HttpClient.getWithPolling(
        {url:url,request:reqInit},
        this.handleAgentQueueResponse.bind(this),
        startAgentQueuesPolling
      );
    }
  }

  /**
   * Callback method which will passed on to the worker and will be executed after the polling api response
   * then will publish to the subject subscriber with the agent queue data
   * @param response - agent queue api response object
   * @example -
   * ```
   * handleAgentQueueResponse(data);
   * ```
   */
  private handleAgentQueueResponse(response: any) {
    if (response) {
      const agentQueues: AgentQueues = this.formatAgentQueueResponse(response);
      if (Object.keys(agentQueues).length > 0)
        this.cxoneClient.agentQueueSubject.next(agentQueues);
    }
  }

  /**
   * This method to format agent queues api response and return the agent queue model object
   * @param response -  agent queue api response object
   * @returns - agent queue
   * @example -
   * ```
   * formatAgentQueueResponse(response);
   * ```
   */
  private formatAgentQueueResponse(response: any): AgentQueues {
    const agentQueues = {} as AgentQueues;
    if (response?.resultSet?.queues && response.resultSet.queues.length > 0) {
      response.resultSet.queues.forEach((queue: { [key: string]: string }) => {
        const queueData = new Queue();
        queueData.parse(queue);
        agentQueues[queue.skillId] = queueData;
      });
    }
    return agentQueues;
  }

  /**
   * Use to initializing the util worker and will return the method inside the worker
   * @example
   * ```
   * this.initAgentQueuesWorker();
   * ```
   */
  public initAgentQueuesWorker() {
    if (!this.pollingWorker)
      this.pollingWorker = new Worker('../worker/cxone-agent-queues-worker', {
        name: 'ccf-agent-queues-polling-worker',
        type: 'module',
      });
    return wrap<AgentQueuesWorker>(this.pollingWorker);
  }

  /**
   * Use to terminate the agent queue worker
   * @example -
   * ```
   * this.terminatePolling
   * ```
   */
  public terminatePolling() {
    this.pollingWorker.terminate();
    this.pollingWorker = undefined;
  }
}
